'use client';

import { useId } from 'react';

import { FontSizes, FontWeights, Text, TextBaseProps } from '@calm-web/design-system';

import { FormattedMessage, isFormattedMessage, type MessageNode } from '../utils';
import {
	CheckIcon,
	CircleIcon,
	List,
	ListItem,
	ValuePropBodyText,
	ValuePropHeaderText,
	ValuePropTextContainer,
	Wrapper,
} from './styles';

export type ValueProp = {
	header?: MessageNode;
	body?: MessageNode;
};

export type ValuePropsListProps = {
	className?: string;
	'data-testid'?: string;
	id?: string;

	/**
	 * @defaultValue
	 * ```tsx
	 * false
	 * ```
	 */
	isOrdered?: boolean;

	/**
	 * @defaultValue
	 * ```tsx
	 * "20px"
	 * ```
	 */
	gapSize?: string;

	/**
	 * @defaultValue
	 * ```tsx
	 * undefined
	 * ```
	 */
	title?: FormattedMessage | null;

	/**
	 * @defaultValue
	 * ```tsx
	 * {
	 *   align: "left",
	 *   color: "black",
	 *   size: FontSizes["2xl"],
	 *   weight: FontWeights.Medium
	 * }
	 * ```
	 */
	titleProps?: TextBaseProps;

	/**
	 * @defaultValue
	 * ```tsx
	 * undefined
	 * ```
	 */
	subtitle?: FormattedMessage | null;

	/**
	 * @defaultValue
	 * ```tsx
	 * {
	 *   color: "blackAlpha60",
	 *   size: FontSizes.lg,
	 *   weight: FontWeights.Medium
	 * }
	 * ```
	 */
	subtitleProps?: TextBaseProps;

	/**
	 * @defaultValue
	 * ```tsx
	 * []
	 * ```
	 */
	valueProps?: ValueProp[];

	/**
	 * @defaultValue
	 * ```tsx
	 * {
	 *   $color: "blue7",
	 * }
	 * ```
	 */
	valuePropIconProps?: {
		$color: string;
	};

	/**
	 * @defaultValue
	 * ```tsx
	 * {
	 *   color: "black",
	 *   noMargin: true,
	 *   size: FontSizes.base,
	 *   weight: FontWeights.Demi
	 * }
	 * ```
	 */
	valuePropHeaderProps?: TextBaseProps;

	/**
	 * @defaultValue
	 * ```tsx
	 * {
	 *   color: "black",
	 *   size: FontSizes.base,
	 * }
	 * ```
	 */
	valuePropBodyProps?: TextBaseProps;
};

export default function ValuePropsList({
	className,
	'data-testid': testId = 'sisu-value-props-list',

	isOrdered = false,

	gapSize = '20px',

	title = null,
	titleProps,

	subtitle = null,
	subtitleProps,

	valueProps = [],
	valuePropIconProps,
	valuePropHeaderProps,
	valuePropBodyProps,
}: ValuePropsListProps) {
	const id = useId();

	const renderValuePropIcon = (index: number) => {
		const num = index + 1;
		const valuePropsIconProps = {
			$color: 'blue7',
			...valuePropIconProps,
			'aria-hidden': true,
		};
		if (isOrdered) {
			return (
				<CircleIcon {...valuePropsIconProps} data-testid={`value-prop-circle-icon-${num}`}>
					{num}
				</CircleIcon>
			);
		}
		return <CheckIcon {...valuePropsIconProps} data-testid={`value-prop-check-icon-${num}`} />;
	};

	return (
		<Wrapper className={className} data-testid={testId} hasTitle={!!title} isRevampRedempFlow>
			{title && (
				<Text
					align="left"
					color="black"
					size={FontSizes['2xl']}
					weight={FontWeights.Medium}
					{...titleProps}
					el="h3"
					id={`${id}-title`}
				>
					{title}
				</Text>
			)}
			{subtitle && (
				<Text color="blackAlpha60" size={FontSizes.lg} weight={FontWeights.Medium} {...subtitleProps}>
					{subtitle}
				</Text>
			)}
			{valueProps.length > 0 && (
				<List aria-labelledby={title ? `${id}-title` : undefined} gapSize={gapSize} isOrdered={isOrdered}>
					{valueProps.map((valueProp, index) => {
						const key = `${id}-list-item-${index}`;
						return (
							<ListItem key={key}>
								{renderValuePropIcon(index)}
								<ValuePropTextContainer propPadding={false}>
									{valueProp.header && isFormattedMessage(valueProp.header) ? (
										<ValuePropHeaderText
											color="black"
											noMargin
											size={FontSizes.base}
											weight={FontWeights.Demi}
											{...valuePropHeaderProps}
											el="p"
										>
											{valueProp.header}
										</ValuePropHeaderText>
									) : (
										valueProp.header
									)}
									{valueProp.body && isFormattedMessage(valueProp.body) ? (
										<ValuePropBodyText color="black" size={FontSizes.base} {...valuePropBodyProps} el="p">
											{valueProp.body}
										</ValuePropBodyText>
									) : (
										valueProp.body
									)}
								</ValuePropTextContainer>
							</ListItem>
						);
					})}
				</List>
			)}
		</Wrapper>
	);
}
